// @ts-nocheck -- Transitional typing for a legacy JavaScript module.
import { useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import AppDialog from "@/components/common/AppDialog";
import ModalActions from "@/components/common/ModalActions";
import { FormCustomField, FormInput, FormSelect } from "@/components/forms/FormControls";
import { Button, buttonVariants } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { usePostUploadDocument } from "@/hooks/useFiles";
import { documentAudienceForOwner, mergeUploadedDocument } from "@/utils/deals.utils";
import { documentSchema } from "@/schemas/deals";
import { DOCUMENT_CATEGORIES } from "@/constant/deals-form.constant";
import { AUDIENCE_LABELS } from "@/constant/deals.constant";
import { DealAssigneeRole } from "@/enum/common.enum";

// A document belongs to one of the roles the server accepts; co-agent and
// vendor are deal roles, not document owners, and offering them failed the save.
const OWNER_ROLES = Object.values(DealAssigneeRole);
const AUDIENCE_OPTIONS = Object.entries(AUDIENCE_LABELS).map(([value, label]) => ({ value, label }));

export default function DocumentDialog({ open, onClose, onSubmit, defaultValues, roles, requestedFor }) {
  const form = useForm({ resolver: zodResolver(documentSchema), defaultValues });
  const { mutate: uploadDocument, isPending: uploading } = usePostUploadDocument();
  const [saving, setSaving] = useState(false);
  const submitLocked = useRef(false);
  const busy = uploading || saving;
  const storagePath = form.watch("storage_path");
  const ownerRole = form.watch("owner_role");
  // Read during render so react-hook-form tracks it; true once the agent picks.
  const audienceChosen = Boolean(form.formState.dirtyFields.audience);

  useEffect(() => {
    if (open) form.reset(defaultValues);
  }, [defaultValues, form, open]);

  // Picking an owner suggests who sees it, until the agent chooses for themselves.
  useEffect(() => {
    if (!audienceChosen) form.setValue("audience", documentAudienceForOwner(ownerRole));
  }, [audienceChosen, form, ownerRole]);

  const upload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    uploadDocument(file, {
      onSuccess: (res) => {
        const uploaded = mergeUploadedDocument(form.getValues(), res.data);
        Object.entries(uploaded).forEach(([key, value]) => form.setValue(key, value, { shouldValidate: true }));
        toast.success("File attached — set a name/category and save");
      },
    });
  };

  const clearUpload = () => {
    ["storage_path", "content_type", "size", "original_filename"].forEach((key) => form.setValue(key, null));
    form.trigger("url");
  };

  const submit = async (values) => {
    if (submitLocked.current || uploading) return;
    submitLocked.current = true;
    setSaving(true);
    try {
      await onSubmit(values);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save document");
    } finally {
      submitLocked.current = false;
      setSaving(false);
    }
  };

  return (
    <AppDialog open={open} onOpenChange={(next) => { if (!next && !busy) onClose(); }} title="Add a document" eyebrow="New" contentClassName="sm:max-w-lg" testId="deal-document-dialog">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(submit)} className="flex flex-col gap-5" data-testid="doc-modal" noValidate>
          <FormCustomField name="storage_path" label="File">
            {() => (
              <div className="border border-dashed border-edge bg-bone/50 p-5 text-center" data-testid="doc-upload-zone">
                {storagePath ? (
                  <div className="flex flex-col gap-2">
                    <p className="truncate text-sm text-ink">✓ {form.watch("original_filename")}</p>
                    <p className="overline text-stone">{form.watch("content_type")} · {Math.round((form.watch("size") || 0) / 1024)} KB</p>
                    <Button variant="seamless" type="button" onClick={clearUpload} className="text-xs text-stone underline underline-offset-4 hover:text-rust" data-testid="doc-clear-upload">Remove file</Button>
                  </div>
                ) : (
                  <label className="inline-flex cursor-pointer flex-col items-center gap-2">
                    <Input type="file" onChange={upload} className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.jpg,.jpeg,.png,.webp,.heic,.heif" disabled={uploading} data-testid="doc-upload-input" />
                    <span className={buttonVariants({ variant: "dashboardPrimary", className: uploading && "pointer-events-none opacity-50" })}><Plus data-icon="inline-start" /> {uploading ? "Uploading…" : "Upload file"}</span>
                    <span className="overline text-stone">PDF · DOCX · XLSX · Images · up to 25MB</span>
                  </label>
                )}
              </div>
            )}
          </FormCustomField>
          <FormInput name="name" label="Name" placeholder="e.g. Purchase Agreement" data-testid="doc-name" required />
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormSelect name="category" label="Category" options={DOCUMENT_CATEGORIES} data-testid="doc-category" />
            <FormSelect name="owner_role" label="Owner" options={OWNER_ROLES.filter((role) => roles[role]).map((role) => ({ value: role, label: roles[role].label }))} data-testid="doc-owner" />
          </div>
          <FormSelect name="audience" label="Who can see this?" options={AUDIENCE_OPTIONS} data-testid="doc-audience" />
          {requestedFor && roles[requestedFor] && (
            <p className="-mt-2 text-xs text-stone" data-testid="doc-requested-for">
              This document will be {requestedFor === "agent"
                ? "kept in your agency workspace"
                : `shared with the ${roles[requestedFor].label.toLowerCase()} portal`}.
            </p>
          )}
          {!storagePath && <FormInput name="url" label="Or paste a URL" type="url" placeholder="https://drive.google.com/…" data-testid="doc-url" />}
          <ModalActions onCancel={onClose} busy={busy} busyLabel={uploading ? "Uploading…" : "Saving…"} submitId="doc-submit" />
        </form>
      </Form>
    </AppDialog>
  );
}
